import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";
import { useMediaQuery } from "react-responsive";
import {
  emotionEmojiImgMapping,
  getAbbreviation,
  barEmotionOrder,
} from "../utils";

const BarGraph2 = ({ barGraphWithPercent, tally, CustomTooltip, title }) => {
  const isChromebookLabel = useMediaQuery({ minWidth: 1201, maxWidth: 1280 });
  const isMobile = useMediaQuery({ maxWidth: 640 });

  const CustomBarLabel = ({ x, y, width, height, value, fill, name }) => {
    const isTwoBarChart = barGraphWithPercent.some((data) => "compare" in data);
    if (!isTwoBarChart) {
      return (
        <text
          x={x + width / 2}
          y={y + height / 2}
          fill="#ffffff"
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
          fontSize="12px"
        >
          {name}
        </text>
      );
    }
  };

  const renderCustomAxisTick = ({ x, y, payload }) => {
    let img_src = emotionEmojiImgMapping[payload.value]["src"];
    let img_alt = emotionEmojiImgMapping[payload.value]["alt"];
    let displayText = isMobile ? getAbbreviation(payload.value) : payload.value;

    const imgSize = 50;
    const imgOffset = isMobile ? 10 : 17;

    return (
      <g transform={`translate(${x},${y})`}>
        <svg
          x={-imgOffset}
          y={0}
          width={50}
          height={50}
          overflow="visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          <image
            x={0}
            y={0}
            width="100%"
            height="100%"
            alt={img_alt}
            href={img_src}
            className="w-[30px] h-[30px] mt-2"
          />
        </svg>
        <text
          className={`capitalize text-[${isChromebookLabel ? "12px" : "14px"}] text-400`}
          x={0}
          y={0}
          dy={50}
          textAnchor="middle"
          fill="#666"
        >
          {displayText}
        </text>
      </g>
    );
  };

  const barColors = ["#5b9cd6", "#4cc68d", "#bd92e4"];
  const barColorsGray = ["#D3D3D3"];

  const hasCompareData = barGraphWithPercent.some((data) => data.compare);
  const hasCountData = barGraphWithPercent.some((data) => data.count);

  const maxCurrentPercent = Math.max(
    ...barGraphWithPercent.map((d) => d.currentPercent || 0)
  );
  const maxComparePercent = Math.max(
    ...barGraphWithPercent.map((d) => d.comparePercent || 0)
  );
  const maxPercent = Math.max(maxCurrentPercent, maxComparePercent);
  const yAxisMax = Math.ceil((maxPercent + 5) / 10) * 10;
  const orderedBarGraphData = barEmotionOrder
    .map((emotion) => barGraphWithPercent.find((e) => e.name === emotion))
    .filter(Boolean);

  return (
    <div className="w-full h-full">
      {title && <div className="text-center text-xl mb-2">{title}</div>}
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={orderedBarGraphData}
          margin={{ top: 20, right: 20, left: 20, bottom: 50 }}
        >
          {isMobile ? (
            <Tooltip
              trigger="click"
              wrapperStyle={{ pointerEvents: "auto" }}
              content={
                <CustomTooltip tally={tally} data={orderedBarGraphData} />
              }
              cursor={{ fill: "lightgray", fillOpacity: "30%" }}
            />
          ) : (
            <Tooltip
              wrapperStyle={{ pointerEvents: "auto" }}
              content={
                <CustomTooltip tally={tally} data={orderedBarGraphData} />
              }
              cursor={{ fill: "lightgray", fillOpacity: "30%" }}
            />
          )}
          {hasCompareData ? (
            <>
              <YAxis
                tickFormatter={(tick) => `${tick}%`}
                domain={[
                  0,
                  (dataMax) => Math.min(100, Math.ceil(dataMax / 10) * 10),
                ]}
                interval={0}
                axisLine={false}
                tickLine={false}
              />
              <CartesianGrid vertical={false} />
            </>
          ) : (
            <YAxis hide={true} />
          )}

          <Bar
            maxBarSize={50}
            dataKey={
              hasCountData && !hasCompareData
                ? "count"
                : hasCompareData
                ? "currentPercent"
                : "current"
            }
            label={isMobile ? <CustomBarLabel /> : null}
          >
            {orderedBarGraphData.map((entry, index) => (
              <Cell key={index} fill={barColors[Math.floor(index / 3)]} />
            ))}
            {!hasCompareData && (
              <LabelList
                dataKey="currentPercent"
                position="top"
                formatter={(val) => `${val}%`}
                fill="#595959"
                fontSize={14}
                fontWeight={700}
              />
            )}
          </Bar>
          {hasCompareData && (
            <Bar maxBarSize={50} dataKey="comparePercent">
              {orderedBarGraphData.map((entry, index) => (
                <Cell key={index} fill={barColorsGray[0]} />
              ))}
            </Bar>
          )}
          <XAxis
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={renderCustomAxisTick}
            dataKey="name"
            minTickGap={-2}
            dy={10}
            offset={0}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarGraph2;
